import { createSelector } from 'reselect'
import moment from 'moment'
import includes from 'lodash/includes'
import { sortProvidersByAvailability } from '../../util/provider'
import { RECOMMENDED_PROVIDER_COUNT } from '../../util/constants'

export const getRecords = (state) => state.providers.records
export const getMeta = (state) => state.providers.meta
export const getProviders = createSelector(getRecords, (records) => Object.values(records))

export const getAvailableProviders = ({ location, visitType, modality } = {}) =>
  createSelector(getProviders, (records = []) => {
    return records
      .filter((provider) => {
        if (!provider.schedule?.available || !provider.schedulable) return false
        return (
          provider.schedulable.visitType === visitType &&
          provider.schedulable.location === location &&
          includes(provider.modalities, modality)
        )
      })
      .sort(sortProvidersByAvailability)
  })

export const getSectionedAvailableProviders = ({ location, visitType, modality }) =>
  createSelector(getAvailableProviders({ location, visitType, modality }), (providers = []) => {
    return [...providers].reduce(
      (acc, curr) => {
        if (acc.recommended.length < RECOMMENDED_PROVIDER_COUNT && curr.isRecommended) {
          acc.recommended.push(curr)
        } else {
          acc.general.push(curr)
        }
        return acc
      },
      { recommended: [], general: [] },
    )
  })

export const getProvider = (providerId) => createSelector(getRecords, (records = {}) => records[providerId])

export const getProviderName = (providerId) =>
  createSelector(getProvider(providerId), (provider) => provider && `${provider.firstName} ${provider.lastName}`)

const credentialsExpansion = (credentials) => {
  if (credentials.replaceAll) {
    return credentials.replaceAll(',', ', ')
  }
  return credentials
}

export const getProviderNameAndCredentials = (providerId) =>
  createSelector(getProvider(providerId), (provider) => {
    if (!provider) return null
    let name
    if (provider.prefix) name = `${provider.prefix} `
    name += `${provider.firstName} ${provider.lastName}`
    if (provider.credentials) name += `, ${credentialsExpansion(provider.credentials)}`
    return name
  })

export const getProviderNameObject = (providerId) =>
  createSelector(getProvider(providerId), (provider = {}) => ({
    id: provider.providerId,
    firstName: provider.firstName,
    lastName: provider.lastName,
  }))

export const getProviderLastFetch = (providerId) => createSelector(getMeta, (meta = {}) => meta.fetches[providerId])

export const isProviderFetching = (providerId) =>
  createSelector(getMeta, (meta = {}) => meta.fetching.includes(providerId))

export const getProviderFetchError = (providerId) =>
  createSelector(getMeta, (meta = {}) => meta.fetches.errors[providerId])

export const getProviderType = (providerId) =>
  createSelector(getProvider(providerId), (provider) => {
    if (!provider) return ''
    const now = moment()
    return provider.licenses.reduce((acc, license) => {
      const expirationDate = moment(license.expDate, 'YYYY-MM-DD')
      if (expirationDate.isAfter(now)) {
        const licenseType = license.type === 'medical' ? 'Physician' : 'Therapist'
        if (!acc.includes(licenseType)) {
          if (!acc) {
            acc = licenseType
          } else {
            acc += `, ${licenseType}`
          }
        }
      }
      return acc
    }, '')
  })
